import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface NotificationItem {
  notification_id: string;
  message: string;
  notification_type: string;
  delivery_status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  created_at: string;
  action_type?: string;
  entity_type?: string;
  metadata?: any;
  read_at?: string | null;
}

interface NotificationState {
  items: NotificationItem[];
  unreadCount: number;
  isOpen: boolean;
  connectionStatus: 'connected' | 'disconnected' | 'connecting';
  isLoading: boolean;
}

const initialState: NotificationState = {
  items: [],
  unreadCount: 0,
  isOpen: false,
  connectionStatus: 'disconnected',
  isLoading: false,
};

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    // Call this when the initial list is fetched from API
    setNotifications: (state, action: PayloadAction<NotificationItem[]>) => {
      state.items = action.payload;
      state.unreadCount = action.payload.filter((n) => n.delivery_status !== 'read').length;
      state.isLoading = false;
    },
    // Call this when a new event arrives via SSE
    addNotification: (state, action: PayloadAction<NotificationItem>) => {
      // Add to top of list
      state.items.unshift(action.payload);
      state.unreadCount += 1;
    },
    // Optimistic update for marking as read
    markAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.items.find((n) => n.notification_id === action.payload);
      if (notification && notification.delivery_status !== 'read') {
        notification.delivery_status = 'read';
        notification.read_at = new Date().toISOString();
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    markAllAsRead: (state) => {
      state.items.forEach((n) => {
        n.delivery_status = 'read';
        n.read_at = new Date().toISOString();
      });
      state.unreadCount = 0;
    },
    togglePanel: (state) => {
      state.isOpen = !state.isOpen;
    },
    setPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.isOpen = action.payload;
    },
    setConnectionStatus: (state, action: PayloadAction<NotificationState['connectionStatus']>) => {
      state.connectionStatus = action.payload;
    },
  },
});

export const {
  setNotifications,
  addNotification,
  markAsRead,
  markAllAsRead,
  togglePanel,
  setPanelOpen,
  setConnectionStatus,
} = notificationSlice.actions;

export default notificationSlice.reducer;
